/**
 * Универсальный пикер «марка / модель» с поиском EN/RU.
 *
 * Используется в OrderDrawer для полей «Марка» и «Модель». Триггер
 * выглядит как обычный `Select` (тот же класс `stryzh-select-trigger`),
 * но dropdown содержит сверху поле поиска, а ниже — отфильтрованный
 * список. Данные подгружаются динамически из `@/data/vehicleDictionary`
 * (отдельный chunk).
 *
 * Контракт `onChange` отдаёт **строку имени** марки/модели — для
 * прямой записи в `orders.brand` / `orders.model` (текстовые поля БД,
 * см. обсуждение в `CHANGELOG_ADMIN.md`). Дополнительно передаётся id
 * — нужен в paired-пикере моделей для подгрузки списка по `brandId`.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { SearchInput } from '@/components/ui/SearchInput'
import {
  type VehicleBrand,
  type VehicleModel,
  searchBrands,
  searchModels,
  listBrands,
  modelsOfBrand,
} from '@/data/vehicleDictionary'

type Item = VehicleBrand | VehicleModel

interface BaseProps {
  selectedName: string
  invalid?: boolean
  disabled?: boolean
  placeholder: string
}

interface BrandProps extends BaseProps {
  kind: 'brand'
  onChange: (value: { id: number; name: string }) => void
}

interface ModelProps extends BaseProps {
  kind: 'model'
  /** Без brandId список моделей загрузить нечем. Если `null` — компонент
   *  становится disabled и показывает `placeholder`. */
  brandId: number | null
  onChange: (value: { id: number; name: string }) => void
}

export type VehicleSelectProps = BrandProps | ModelProps

export function VehicleSelect(props: VehicleSelectProps) {
  const { t } = useTranslation()
  const { selectedName, invalid, placeholder } = props
  const disabled =
    props.disabled || (props.kind === 'model' && props.brandId == null)

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<Item[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  // Защита от гонок при быстром наборе — поздние ответы с устаревшим id
  // игнорируем.
  const searchIdRef = useRef(0)

  // Стабильный ключ зависимостей для эффектов: для модели — id марки,
  // для марки — фиксированная константа.
  const brandIdKey = props.kind === 'model' ? props.brandId : 'brand'

  // Закрытие по клику снаружи и Escape.
  useEffect(() => {
    if (!open) return
    function onPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Сброс query при открытии. При смене марки (для модели) — полная
  // очистка, чтобы старый список не мелькнул. Автофокус ставит сам
  // SearchInput через `autoFocus`. setState в effect здесь сознательно —
  // как и в `Select.tsx`, см. комментарий там про измерение DOM.
  useEffect(() => {
    if (!open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuery('')
  }, [open, brandIdKey])

  // Первичная загрузка и реакция на изменение query / brandId. setState
  // в effect здесь — переход к async-загрузке (loading/error), это
  // допустимый и обычный паттерн, react-compiler ругается формально.
  useEffect(() => {
    if (!open) return
    const id = ++searchIdRef.current
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setError(null)
    const promise: Promise<Item[]> =
      props.kind === 'brand'
        ? query
          ? searchBrands(query)
          : listBrands()
        : props.brandId == null
          ? Promise.resolve([])
          : query
            ? searchModels(props.brandId, query)
            : modelsOfBrand(props.brandId)
    promise
      .then((list) => {
        if (cancelled || id !== searchIdRef.current) return
        setItems(list)
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (cancelled || id !== searchIdRef.current) return
        setError(e instanceof Error ? e.message : t('vehicle.loadError'))
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, query, brandIdKey, props])

  const triggerLabel = useMemo(
    () => selectedName || placeholder,
    [selectedName, placeholder],
  )
  const isEmpty = !selectedName

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={clsx(
          'stryzh-select-trigger',
          open && 'stryzh-select-trigger--open',
          invalid && 'stryzh-select-trigger--error',
        )}
        onClick={() => {
          if (disabled) return
          setOpen((v) => !v)
        }}
      >
        <span className={clsx(isEmpty && 'stryzh-select-trigger__placeholder')}>
          {triggerLabel}
        </span>
        <span
          className={clsx(
            'stryzh-select-trigger__chevron',
            open && 'stryzh-select-trigger__chevron--open',
          )}
          aria-hidden
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 4L6 8L10 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open ? (
        <div
          className="stryzh-select-dropdown"
          style={{ maxHeight: 360, display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ padding: 8, borderBottom: '1px solid var(--stryzh-color-border-subtle, rgba(255,255,255,0.08))' }}>
            <SearchInput
              value={query}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setQuery(e.currentTarget.value)
              }
              placeholder={t('vehicle.searchPlaceholder')}
              autoFocus
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div className="stryzh-select-option" style={{ opacity: 0.6 }}>
                {t('common.loading')}
              </div>
            ) : error ? (
              <div className="stryzh-select-option" style={{ opacity: 0.6 }}>
                {error}
              </div>
            ) : items.length === 0 ? (
              <div className="stryzh-select-option" style={{ opacity: 0.6 }}>
                {t('vehicle.notFound')}
              </div>
            ) : (
              items.map((it) => {
                const isSelected = it.name === selectedName
                const subtitle =
                  props.kind === 'model'
                    ? formatYears(it as VehicleModel)
                    : null
                return (
                  <button
                    key={it.id}
                    type="button"
                    className={clsx(
                      'stryzh-select-option',
                      isSelected && 'stryzh-select-option--selected',
                    )}
                    onMouseDown={(e) => {
                      // Не отнимаем фокус у input до момента onChange.
                      e.preventDefault()
                      e.stopPropagation()
                      props.onChange({ id: it.id, name: it.name })
                      setOpen(false)
                    }}
                  >
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span>{it.name}</span>
                      {subtitle ? (
                        <span style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>
                          {subtitle}
                        </span>
                      ) : null}
                    </span>
                    {isSelected ? (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        className="stryzh-select-option__check"
                        aria-hidden
                      >
                        <path
                          d="M2.5 7L5.5 10L11.5 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                  </button>
                )
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function formatYears(m: VehicleModel): string | null {
  const { yf, yt } = m
  if (yf == null && yt == null) return null
  if (yf != null && yt != null) {
    return yf === yt ? `${yf}` : `${yf}–${yt}`
  }
  return String(yf ?? yt)
}
